import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getEstructura } from "../services/estructuraService";
import { TIPO, soloValidos } from "../utils/estructura";

// Columna de la grilla por CÓDIGO de tipo.
export const COLUMNA = { DIR: 1, D: 2, I: 3, U: 4, C: 5, P: 6, O: 6, R: 6 };
// Orden de los hijos de un nodo (para que en la cascada salgan por columna).
const ORDEN_HIJO = { D: 0, I: 1, U: 2, C: 3, P: 4, O: 5, R: 6 };

// Nodo sintético "Dirección y Staff": raíz de la cascada y padre de TODOS los
// departamentos. NO viene del back (servicio pendiente); se fabrica en el front.
//
// Su tipo es "DIR" y NO la "H" del back a proposito: si el Get algun dia devuelve el
// nodo de Direccion, no colisiona con la card que fabrica el front.
export const DIR_ID = "__DIR__";
export const TIPO_DIR = "DIR";

/**
 * Árbol de estructura del hospital para la vista en CASCADA (multi-expansión).
 *
 * A diferencia del drill-down lazy, trae TODA la estructura del hospital en una
 * sola llamada (`listAll`) y arma el árbol client-side (como el `estructuras`
 * original). Permite expandir varias ramas a la vez y calcular el layout en
 * cascada (cada nodo ocupa su propia fila de grilla; el escalonado sale del DFS).
 *
 * Jerarquía reconstruida por ids de dominio (modelo flexible "todo directo"): cada nodo
 * cuelga de su ancestro MÁS PROFUNDO presente (dirección → depto → división → unidad).
 * CA/PU/PO cuelgan de su ancestro no-establecimiento más profundo. La salida del Get NO
 * trae un establecimiento-padre, así que puesto/posta debajo de un CAP no se reconstruye aún.
 *
 * NOTA: `listAll: "Y"` es un supuesto a confirmar con el back.
 *
 * @param {number|string|null} hospitalId
 * @param {object} credentials
 */
export function useArbolHospital(hospitalId, credentials) {
    const [nodos, setNodos] = useState([]);
    const [expandido, setExpandido] = useState(() => new Set());
    const [seleccion, setSeleccion] = useState({ id: null, tipo: null });
    const [cargando, setCargando] = useState(false);
    const [error, setError] = useState(false);
    const reqRef = useRef(0);

    // Carga toda la estructura. `preservar` = mantener expansión/selección (refresh
    // tras asignar); si no, se resetean (cambio de hospital / recarga total).
    const cargar = useCallback(
        (preservar = false) => {
            if (!hospitalId) {
                setNodos([]);
                setExpandido(new Set());
                setSeleccion({ id: null, tipo: null });
                setError(false);
                setCargando(false);
                return;
            }
            const req = reqRef.current + 1;
            reqRef.current = req;
            setCargando(true);
            setError(false);
            getEstructura(credentials, { rnhEstablecimientoEstructuraHospitalId: hospitalId, listAll: "Y" })
                .then((res) => {
                    if (reqRef.current !== req) return;
                    setNodos(soloValidos(res.data));
                    if (!preservar) {
                        setExpandido(new Set());
                        setSeleccion({ id: null, tipo: null });
                    }
                })
                .catch(() => {
                    if (reqRef.current === req) setError(true);
                })
                .finally(() => {
                    if (reqRef.current === req) setCargando(false);
                });
        },
        [hospitalId, credentials]
    );

    useEffect(() => {
        cargar(false);
    }, [cargar]);

    // Reconstrucción del árbol: hijos por id de nodo + raíces + qué nodos tienen hijos.
    const { roots, hijosPorId, padrePorId, idsConHijos } = useMemo(() => {
        const deById = new Map(); // rnhDepartamentoId → nodo DE
        const diById = new Map(); // rnhDivisionId → nodo DI
        const unById = new Map(); // rnhUnidadId → nodo UN
        const caById = new Map(); // rnhCapsId → nodo CA (su propio id)
        nodos.forEach((n) => {
            if (n.rnhTipoEstructuraCod === TIPO.DEPARTAMENTO) deById.set(n.rnhDepartamentoId, n);
            else if (n.rnhTipoEstructuraCod === TIPO.DIVISION) diById.set(n.rnhDivisionId, n);
            else if (n.rnhTipoEstructuraCod === TIPO.UNIDAD) unById.set(n.rnhUnidadId, n);
            else if (n.rnhTipoEstructuraCod === TIPO.CAPS) caById.set(n.rnhCapsId, n);
        });

        // Padre = el ancestro MÁS PROFUNDO presente. Cada nodo trae en sus ids de dominio la
        // cadena hasta su nivel (incluido rnhCapsId: para un CAP es su propio id; para un
        // puesto/posta hijo de un cap es el id del cap padre). Se recorre de más profundo a
        // más superficial salteando el nivel propio del nodo.
        const NIVELES = [
            { id: (n) => n.rnhCapsId, map: caById },
            { id: (n) => n.rnhUnidadId, map: unById },
            { id: (n) => n.rnhDivisionId, map: diById },
            { id: (n) => n.rnhDepartamentoId, map: deById },
        ];
        const DESDE = { P: 0, O: 0, R: 0, C: 1, U: 2, I: 3, D: 4 };
        const padreId = (n) => {
            for (let i = DESDE[n.rnhTipoEstructuraCod] ?? 0; i < NIVELES.length; i++) {
                const id = NIVELES[i].id(n);
                if (id != null) {
                    const p = NIVELES[i].map.get(id);
                    if (p) return p.rnhEstablecimientoEstructuraId;
                }
            }
            return null; // sin ancestro → cuelga de la Dirección
        };

        const hijosPorId = new Map();
        const padrePorId = new Map(); // estructuraId del nodo → estructuraId del padre
        const sinPadre = []; // sin ancestro: deptos, o lo asignado DIRECTO al hospital
        nodos.forEach((n) => {
            const pid = padreId(n);
            padrePorId.set(n.rnhEstablecimientoEstructuraId, pid);
            if (pid == null) {
                sinPadre.push(n);
                return;
            }
            if (!hijosPorId.has(pid)) hijosPorId.set(pid, []);
            hijosPorId.get(pid).push(n);
        });

        // Raíz sintética "Dirección y Staff": aparece siempre que haya un hospital filtrado
        // y es el padre de TODO lo que no tiene ancestro (deptos, o lo asignado directo al
        // hospital). Si no hay nada, la card igual aparece pero sin hijos (no expande).
        let rootsFinal = sinPadre;
        if (hospitalId != null) {
            const dir = {
                rnhEstablecimientoEstructuraId: DIR_ID,
                rnhTipoEstructuraCod: TIPO_DIR,
                rnhEstructuraNombre: "Dirección y Staff",
            };
            if (sinPadre.length > 0) {
                sinPadre.forEach((d) => padrePorId.set(d.rnhEstablecimientoEstructuraId, DIR_ID));
                hijosPorId.set(DIR_ID, sinPadre.slice());
            }
            padrePorId.set(DIR_ID, null);
            rootsFinal = [dir];
        }

        // Ordenar los hijos de cada nodo por columna (para que salgan agrupados por tipo).
        hijosPorId.forEach((arr) =>
            arr.sort((a, b) => (ORDEN_HIJO[a.rnhTipoEstructuraCod] ?? 9) - (ORDEN_HIJO[b.rnhTipoEstructuraCod] ?? 9))
        );

        return { roots: rootsFinal, hijosPorId, padrePorId, idsConHijos: new Set(hijosPorId.keys()) };
    }, [nodos, hospitalId]);

    /**
     * Agentes de cada nodo y de su rama entera.
     *
     * `rnhEstablecimientoEstructuraTotalAgentes` viene por nodo en la respuesta del arbol, asi
     * que lo PROPIO es un dato del back. El acumulado lo suma el front recorriendo hacia
     * abajo: es lo que la card muestra cuando esta colapsada, para saber si vale la pena
     * abrirla sin tener que abrirla.
     *
     * Los dos sirven y dicen cosas distintas: "esta unidad tiene 3" no es lo mismo que "de
     * esta unidad para abajo hay 12".
     *
     * La Direccion es una raiz sintetica sin fila propia en la respuesta, asi que su propio
     * conteo es 0 y su acumulado es el total del hospital. Sale solo de la recursion.
     */
    const { agentesPropios, agentesEnRama } = useMemo(() => {
        const propios = new Map();
        const enRama = new Map();

        const sumar = (nodo) => {
            const id = nodo.rnhEstablecimientoEstructuraId;
            const propio = nodo.rnhEstablecimientoEstructuraTotalAgentes ?? 0;
            const total =
                propio +
                (hijosPorId.get(id) ?? []).reduce((acc, hijo) => acc + sumar(hijo), 0);
            propios.set(id, propio);
            enRama.set(id, total);
            return total;
        };
        roots.forEach(sumar);

        return { agentesPropios: propios, agentesEnRama: enRama };
    }, [roots, hijosPorId]);

    // Layout en cascada: el nodo se ubica en su fila y sus hijos arrancan en ESA MISMA
    // fila (el PRIMER hijo comparte fila con el padre); los siguientes bajan según cuánto
    // ocupó el subárbol anterior. `walk` devuelve cuántas filas ocupa el subárbol del nodo.
    const placements = useMemo(() => {
        const out = [];
        const walk = (nodo, startRow) => {
            out.push({ nodo, col: COLUMNA[nodo.rnhTipoEstructuraCod] ?? 2, row: startRow });
            const hijos = expandido.has(nodo.rnhEstablecimientoEstructuraId)
                ? (hijosPorId.get(nodo.rnhEstablecimientoEstructuraId) ?? [])
                : [];
            if (hijos.length === 0) return 1;
            let cursor = startRow;
            hijos.forEach((h) => {
                cursor += walk(h, cursor);
            });
            return cursor - startRow;
        };
        let row = 1;
        roots.forEach((r) => {
            row += walk(r, row);
        });
        return out;
    }, [roots, hijosPorId, expandido]);

    const tieneHijos = useCallback((nodo) => idsConHijos.has(nodo?.rnhEstablecimientoEstructuraId), [idsConHijos]);
    const estaExpandido = useCallback((id) => expandido.has(id), [expandido]);

    const toggleExpandir = useCallback((nodo) => {
        const id = nodo?.rnhEstablecimientoEstructuraId;
        if (id == null) return;
        setExpandido((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    /**
     * Deja a la vista un nodo: lo expande a el y a todos sus ancestros.
     *
     * Es lo que se llama al cerrar el modal de gestion. Asignar le cuelga hijos nuevos al nodo
     * gestionado, y si estaba colapsado —o lo estaba alguno de sus padres— el trabajo recien
     * hecho no se veia en ningun lado y habia que ir abriendo a mano hasta encontrarlo.
     *
     * Se recorre hacia ARRIBA con `padrePorId`: lo asignado siempre entra como hoja, asi que
     * lo que hay que abrir es el camino que lleva hasta ello, no lo que cuelga.
     */
    const expandirCamino = useCallback(
        (id) => {
            if (id == null) return;
            setExpandido((prev) => {
                const next = new Set(prev);
                // `vistos` va aparte de `next` y no se reemplaza por un `next.has()`: un nodo
                // puede estar marcado como expandido con un padre colapsado —colapsar no
                // limpia la marca de los hijos— y cortar ahi dejaria el camino a medias. Esto
                // solo esta para que unos ids inconsistentes no armen un bucle infinito.
                const vistos = new Set();
                let actual = id;
                while (actual != null && !vistos.has(actual)) {
                    vistos.add(actual);
                    next.add(actual);
                    actual = padrePorId.get(actual) ?? null;
                }
                return next;
            });
        },
        [padrePorId]
    );

    const expandirTodo = useCallback(() => setExpandido(new Set(idsConHijos)), [idsConHijos]);
    const colapsarTodo = useCallback(() => setExpandido(new Set()), []);

    const seleccionar = useCallback((nodo) => {
        const id = nodo?.rnhEstablecimientoEstructuraId ?? null;
        const tipo = nodo?.rnhTipoEstructuraCod ?? null;
        setSeleccion((s) => (s.id === id ? { id: null, tipo: null } : { id, tipo }));
    }, []);

    // Busca en placements (incluye la raíz sintética "Dirección y Staff", que no está
    // en `nodos`), para que la Dirección también pueda ser el nodo seleccionado.
    const nodoSeleccionado = useMemo(
        () =>
            seleccion.id == null
                ? null
                : placements.find((p) => p.nodo.rnhEstablecimientoEstructuraId === seleccion.id)?.nodo ?? null,
        [placements, seleccion.id]
    );

    return {
        // Datos / layout
        placements,
        roots,
        hijosPorId, // hijos directos por id de nodo (para el árbol de impresión)
        padrePorId, // relación hijo→padre (para dibujar los conectores)
        hayNodos: nodos.length > 0,
        // Helpers de árbol
        tieneHijos,
        estaExpandido,
        // Agentes: los propios del nodo y los de toda su rama.
        agentesPropios,
        agentesEnRama,
        hayExpandidos: expandido.size > 0,
        // Acciones de expansión
        toggleExpandir,
        expandirCamino,
        expandirTodo,
        colapsarTodo,
        // Selección (target del botón Asignar)
        seleccionId: seleccion.id,
        seleccionTipo: seleccion.tipo,
        nodoSeleccionado,
        seleccionar,
        // Estado / recarga
        cargando,
        error,
        recargar: () => cargar(false),
        refrescar: () => cargar(true),
    };
}
